import React, { useState } from 'react';
import axios from 'axios';

const API = '/api';

interface ReturnItem { name: string; quantity: number; image?: string | null; }
interface BuyerReturn {
  id: string;
  reference_number: string;
  status: string;
  created_at: string | null;
  order_id?: string | null;
  items: ReturnItem[];
}
interface BuyerOrder {
  order_id: string;
  account_id: string;
  account_name: string;
  bought_at: string | null;
  smart: boolean;
  apilo_id: string | null;
  items: ReturnItem[];
}

// Wspolny guzik "Zwroty" (Wiadomosci/Zgloszenia, desktop+mobile).
// ROWNOLEGLE: zgloszone zwroty kupujacego (/returns/by-buyer) + jego zamowienia Allegro (/allegro/buyer-orders).
// Zamowienie ze zgloszonym zwrotem nie jest dublowane. 0 -> komunikat, 1 -> od razu otwiera, wiecej -> modal wyboru.
// Zamowienie bez zwrotu otwierane w module Zwroty przez ref "ORDER:<account_id>:<order_id>".
export default function BuyerReturnsButton({
  buyerLogin,
  variant = 'desktop',
  onOpenReturn,
}: {
  buyerLogin: string;
  variant?: 'desktop' | 'mobile';
  onOpenReturn: (referenceNumber: string) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [modal, setModal] = useState<{ returns: BuyerReturn[]; orders: BuyerOrder[] } | null>(null);

  const statusLabels: { [k: string]: string } = {
    NEW: 'Nowy',
    DELIVERED: 'Dostarczony',
    WAREHOUSE_DELIVERED: 'W magazynie',
    FINISHED: 'Zakończony',
    CANCELLED: 'Anulowany',
    COMMISSION_REFUNDED: 'Zwrot prowizji',
    COMMISSION_REFUND_CLAIMED: 'Wniosek o prowizję',
  };

  const fmtDate = (d: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('pl-PL'); } catch { return ''; }
  };

  const openOrder = (o: BuyerOrder) => onOpenReturn('ORDER:' + o.account_id + ':' + o.order_id);

  const handleClick = async () => {
    if (!buyerLogin || loading) return;
    setMsg('');
    setLoading(true);
    const [rr, oo] = await Promise.allSettled([
      axios.get(`${API}/returns/by-buyer?buyer_login=${encodeURIComponent(buyerLogin)}`),
      axios.get(`${API}/allegro/buyer-orders?buyer_login=${encodeURIComponent(buyerLogin)}&account_id=`),
    ]);
    setLoading(false);
    const returnsList: BuyerReturn[] = rr.status === 'fulfilled' ? (rr.value.data?.returns || []) : [];
    const rows: any[] = oo.status === 'fulfilled' ? (oo.value.data?.orders || []) : [];
    const withReturn = new Set<string>();
    returnsList.forEach(r => { if (r.order_id) withReturn.add(r.order_id); });
    const map: { [id: string]: BuyerOrder } = {};
    const ordersList: BuyerOrder[] = [];
    for (const row of rows) {
      const oid = row.order_id;
      if (!oid || !row.account_id || withReturn.has(oid)) continue;
      if (!map[oid]) {
        map[oid] = { order_id: oid, account_id: row.account_id, account_name: row.account_name || '', bought_at: row.bought_at || null, smart: !!row.smart, apilo_id: row.apilo_id || null, items: [] };
        ordersList.push(map[oid]);
      }
      map[oid].items.push({ name: row.offer_name || '', quantity: row.quantity || 1, image: row.image || null });
    }
    const total = returnsList.length + ordersList.length;
    if (total === 0) {
      if (rr.status === 'rejected' || oo.status === 'rejected') setMsg('Nie udało się pobrać zwrotów/zamówień klienta. Spróbuj ponownie.');
      else setMsg('Ten klient nie ma zamówień ani zgłoszonych zwrotów');
    } else if (total === 1) {
      if (returnsList.length === 1) onOpenReturn(returnsList[0].reference_number);
      else openOrder(ordersList[0]);
    } else {
      setModal({ returns: returnsList, orders: ordersList });
    }
  };

  const renderItems = (items: ReturnItem[]) => (
    (items || []).length > 0
      ? items.map((it, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            {it.image
              ? <img src={it.image} alt='' style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: '1px solid #f3f4f6' }} onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
              : <div style={{ width: 36, height: 36, borderRadius: 6, flexShrink: 0, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>📦</div>}
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
              {it.quantity} × {it.name || '—'}
            </span>
          </div>
        ))
      : <span style={{ color: '#9ca3af' }}>Brak pozycji</span>
  );

  const cardStyle: React.CSSProperties = { border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', marginBottom: 8, cursor: 'pointer', transition: 'background 0.1s' };
  const sectionStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', margin: '10px 0 6px' };

  const btnStyle: React.CSSProperties =
    variant === 'mobile'
      ? { fontSize: 12, padding: '4px 10px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(253,186,147,0.3)', color: '#fff', cursor: 'pointer', fontWeight: 500, marginLeft: 6 }
      : { fontSize: 11, padding: '1px 10px', borderRadius: 10, border: '1px solid #fed7aa', background: '#fff7ed', color: '#c2410c', cursor: 'pointer' };

  return (
    <>
      <button onClick={handleClick} disabled={loading || !buyerLogin} style={btnStyle} title="Zwroty i zamówienia tego klienta">
        {loading ? '⏳' : (variant === 'mobile' ? '📦' : '📦 Zwroty')}
      </button>
      {msg && (
        <div
          onClick={() => setMsg('')}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, padding: '24px 28px', maxWidth: 380, boxShadow: '0 10px 40px rgba(0,0,0,0.2)', textAlign: 'center' }}
          >
            <div style={{ fontSize: 32, marginBottom: 10 }}>📦</div>
            <div style={{ fontSize: 15, color: '#374151', fontWeight: 500, marginBottom: 16 }}>{msg}</div>
            <button
              onClick={() => setMsg('')}
              style={{ fontSize: 13, padding: '8px 20px', borderRadius: 8, border: 'none', background: '#c2410c', color: '#fff', cursor: 'pointer', fontWeight: 500 }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {modal && (
        <div
          onClick={() => setModal(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, padding: 20, maxWidth: 560, width: '100%', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ fontWeight: 600, fontSize: 16 }}>Wybierz zwrot lub zamówienie</div>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>
              Klient {buyerLogin}: zgłoszone zwroty {modal.returns.length}, zamówienia bez zgłoszenia {modal.orders.length}.
            </div>

            {modal.returns.length > 0 && <div style={sectionStyle}>Zgłoszone zwroty</div>}
            {modal.returns.map(r => (
              <div
                key={r.id}
                onClick={() => { onOpenReturn(r.reference_number); setModal(null); }}
                style={cardStyle}
                onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: '#111827' }}>{r.reference_number}</span>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>{fmtDate(r.created_at)}</span>
                </div>
                <div style={{ fontSize: 12, color: '#374151', marginBottom: 6 }}>{renderItems(r.items)}</div>
                <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10, background: '#f3f4f6', color: '#4b5563' }}>
                  {statusLabels[r.status] || r.status}
                </span>
              </div>
            ))}

            {modal.orders.length > 0 && <div style={sectionStyle}>Zamówienia bez zgłoszenia zwrotu</div>}
            {modal.orders.map(o => (
              <div
                key={o.order_id}
                onClick={() => { openOrder(o); setModal(null); }}
                style={cardStyle}
                onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                onMouseLeave={e => (e.currentTarget.style.background = '#fff')}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, color: '#111827' }}>
                    🛒 Zamówienie {fmtDate(o.bought_at)}
                    {o.apilo_id && <span style={{ fontWeight: 500, color: '#6d28d9', marginLeft: 6 }}>· Apilo {o.apilo_id}</span>}
                  </span>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>{o.account_name}</span>
                </div>
                <div style={{ fontSize: 12, color: '#374151', marginBottom: 6 }}>{renderItems(o.items)}</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10, background: '#f0fdf4', color: '#15803d' }}>Możliwy zwrot pieniędzy</span>
                  {o.smart && <span style={{ fontSize: 11, padding: '1px 8px', borderRadius: 10, background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', fontWeight: 600 }}>Smart</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
